import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import RevealLine from "@/components/RevealLine";
import { CompareTable } from "@/components/CompareTable";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(null);
  const { lang, tr } = useLang();

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      if (r.data.length) setActive(r.data[0]);
    });
  }, []);

  const loc = (p, field) => (lang === "en" ? p[`${field}_en`] || p[field] : p[field]);
  const locFeatures = (p) =>
    lang === "en" && p.features_en?.length ? p.features_en : p.features;

  return (
    <div data-testid="page-products" className="bg-white text-[#0a0a0a] pt-32 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-8">
          {tr({ it: "— PRODOTTI / CATALOGO", en: "— PRODUCTS / CATALOGUE" })}
        </div>
        <h1 className="font-display text-[clamp(3rem,10vw,10rem)] leading-[0.86] tracking-tighter max-w-6xl">
          {lang === "en" ? (
            <>
              <div><RevealLine delay={0.2}>Every product</RevealLine></div>
              <div>
                <RevealLine delay={0.35}>is a </RevealLine>{" "}
                <RevealLine delay={0.5} className="font-editorial italic font-normal text-[#3f3f46]">
                  system.
                </RevealLine>
              </div>
            </>
          ) : (
            <>
              <div><RevealLine delay={0.2}>Ogni prodotto</RevealLine></div>
              <div>
                <RevealLine delay={0.35}>è un </RevealLine>{" "}
                <RevealLine delay={0.5} className="font-editorial italic font-normal text-[#3f3f46]">
                  sistema.
                </RevealLine>
              </div>
            </>
          )}
        </h1>
      </section>

      {/* Product showcase — sticky detail with list */}
      <section className="mt-24 border-t border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12">
          {/* Nav list */}
          <div className="lg:col-span-4 border-r border-[#e4e4e7]">
            {products.map((p, i) => (
              <button
                key={p.id}
                data-testid={`product-tab-${p.name.replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => setActive(p)}
                className={`w-full text-left border-b border-[#e4e4e7] px-8 py-10 transition-colors ${
                  active?.id === p.id ? "bg-[#f4f4f5]" : "hover:bg-[#f4f4f5]/60"
                }`}
              >
                <div className="flex items-baseline justify-between mb-3">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-[#3f3f46]">0{i + 1}</span>
                  <span className="font-mono text-[10px] tracking-widest uppercase text-[#52525b]">
                    {loc(p, "category")}
                  </span>
                </div>
                <div
                  className={`font-display text-4xl md:text-5xl tracking-tighter leading-none transition-colors ${
                    active?.id === p.id ? "text-[#007A8A]" : "text-[#0a0a0a]"
                  }`}
                >
                  {p.name}
                </div>
                <div className="font-body text-sm text-[#3f3f46] mt-3">{loc(p, "tagline")}</div>
              </button>
            ))}
          </div>

          {/* Detail */}
          <div className="lg:col-span-8">
            {active && (
              <div className="p-8 md:p-12 lg:p-16">
                <div className="relative aspect-[16/9] overflow-hidden border border-[#e4e4e7] mb-12 spotlight bg-[#0b0b0e]">
                  <img src={active.image} alt={active.name} className="w-full h-full object-contain" />
                  <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.3em] text-white/80 bg-black/50 px-3 py-2 backdrop-blur">
                    {active.name} · {loc(active, "category")?.toUpperCase()}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                  <div className="md:col-span-7">
                    <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-6">
                      {active.name}
                    </h2>
                    <p className="font-editorial italic text-2xl text-[#3f3f46] mb-8">{loc(active, "tagline")}</p>
                    <p className="font-body text-[#3f3f46] leading-relaxed">{loc(active, "description")}</p>

                    {locFeatures(active)?.length > 0 && (
                      <div className="mt-10 space-y-3">
                        <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A]">
                          {tr({ it: "— CARATTERISTICHE", en: "— FEATURES" })}
                        </div>
                        {locFeatures(active).map((f, idx) => (
                          <div
                            key={idx}
                            className="flex gap-4 border-b border-[#e4e4e7] py-3 font-body text-sm text-[#0a0a0a]"
                          >
                            <span className="font-mono text-[10px] text-[#52525b] tabular-nums pt-0.5">
                              {String(idx + 1).padStart(2, "0")}
                            </span>
                            <span>{f}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-5">
                    <div className="border border-[#e4e4e7] bg-[#f4f4f5] p-6 md:p-8">
                      <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-6">— SPEC</div>
                      <div className="divide-y divide-[#e4e4e7]">
                        {active.specs?.map((s) => (
                          <div key={s.label} className="py-3 flex justify-between items-center">
                            <span className="font-mono text-[10px] tracking-widest uppercase text-[#3f3f46]">
                              {s.label}
                            </span>
                            <span className="font-display text-lg tracking-tight">{s.value}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 pt-5 border-t border-[#e4e4e7]">
                        <div className="font-mono text-[10px] tracking-[0.3em] text-[#3f3f46] mb-2">
                          {tr({ it: "PREZZO", en: "PRICE" })}
                        </div>
                        <div className="font-body text-sm text-[#3f3f46]" data-testid="product-price-note">
                          {tr({
                            it: "Su preventivo personalizzato — risposta entro 2 ore.",
                            en: "On personalised quote — reply within 2 hours.",
                          })}
                        </div>
                      </div>
                      <Link
                        to="/preventivo"
                        data-testid={`product-cta-${active.name.replace(/\s/g, "-").toLowerCase()}`}
                        className="btn-primary w-full justify-between mt-8"
                      >
                        {tr({ it: "Richiedi preventivo", en: "Request a quote" })} <ArrowUpRight size={14} />
                      </Link>
                      <Link
                        to="/prova-a-casa"
                        data-testid={`product-trial-cta-${active.name.replace(/\s/g, "-").toLowerCase()}`}
                        className="btn-ghost w-full justify-between mt-3"
                      >
                        {tr({ it: "Prova a casa (1 giorno)", en: "Home trial (1 day)" })} <ArrowUpRight size={14} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="mt-24 border-t border-[#e4e4e7]" data-testid="products-compare-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-24">
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-6">
            {tr({ it: "— CONFRONTA I MODELLI", en: "— COMPARE MODELS" })}
          </div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter mb-16">
            {tr({ it: "La gamma a colpo d'occhio.", en: "The range at a glance." })}
          </h2>
          <CompareTable />
        </div>
      </section>
    </div>
  );
}
